import AtriumApi, { getApiErrorMessage } from "api";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import {
  MdAssignment,
  MdEventAvailable,
  MdFavoriteBorder,
  MdPersonAdd,
} from "react-icons/md";
import Badge from "components/badge/Badge";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { EMPTY, formatDate, formatTime } from "utils/format";
import heartbeatResponseService from "services/heartbeatResponseService";
import heartbeatActionItemService from "services/heartbeatActionItemService";

const ACTION_STATUS_TONES = {
  completed: "success",
  in_progress: "info",
};

const ACTIVITY_ICONS = {
  service: MdEventAvailable,
  heartbeat_survey: MdFavoriteBorder,
  heartbeat_action: MdAssignment,
  account_created: MdPersonAdd,
};

const titleCase = (value) =>
  value?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ActivityDetails = ({ activity }) => {
  const { type, data } = activity;
  if (type === "service") {
    return (
      <>
        <p className="text-sm font-semibold capitalize text-fg">{data?.Service?.name}</p>
        <p className="text-sm text-fg-muted">{data?.Service?.type}</p>
      </>
    );
  }
  if (type === "heartbeat_survey") {
    return (
      <>
        <p className="text-sm font-semibold text-fg">Heartbeat Survey Completed</p>
        <p className="text-sm text-fg-muted">
          {data?.campaign?.name || "Survey"}
          {data?.answers && (
            <span className="ml-2">
              ({Object.keys(data.answers).length} questions answered)
            </span>
          )}
        </p>
      </>
    );
  }
  if (type === "heartbeat_action") {
    return (
      <>
        <p className="text-sm font-semibold text-fg">
          Action Item: {titleCase(data?.actionType) || data?.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
          {data?.status && (
            <Badge tone={ACTION_STATUS_TONES[data.status] || "warning"} size="sm">
              {titleCase(data.status)}
            </Badge>
          )}
          {data?.assignedTo && (
            <span>
              Assigned to {data.assignedTo.firstName} {data.assignedTo.lastName}
            </span>
          )}
        </div>
      </>
    );
  }
  if (type === "account_created") {
    return (
      <>
        <p className="text-sm font-semibold text-fg">Account Created</p>
        <p className="text-sm text-fg-muted">
          {data?.firstTimeGuest ? "First-time guest" : "Member joined"}
        </p>
      </>
    );
  }
  return null;
};

const ActivityTime = ({ activity, today }) => {
  if (activity.type === "service") {
    const { startAt, endAt } = activity.data?.Service || {};
    const start = startAt ? formatTime(`${today} ${startAt}`) : EMPTY;
    const end = endAt ? formatTime(`${today} ${endAt}`) : EMPTY;
    return `${start} to ${end}`;
  }
  if (activity.type === "account_created") return "Account registered";
  return formatTime(activity.date);
};

const History = ({ user }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  const [services, setServices] = React.useState([]);
  const [heartbeatResponses, setHeartbeatResponses] = React.useState([]);
  const [heartbeatActionItems, setHeartbeatActionItems] = React.useState([]);

  useEffect(() => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const fetchHistoryData = async () => {
      try {
        const serviceResponse = await AtriumApi.get(
          `/v2/logs/service?userId=${user.id}`
        );

        if (serviceResponse) {
          const servicesData = serviceResponse?.data?.data?.data;
          setServices(servicesData);
        }

        // Survey responses and action items are optional; a failure leaves them empty.
        try {
          const responses = await heartbeatResponseService.getResponsesByUser(user.id);
          if (responses.success) {
            setHeartbeatResponses(responses.data || []);
          }
        } catch (responseError) {
          console.log("No heartbeat survey responses found:", responseError.message);
        }

        try {
          const actionItems = await heartbeatActionItemService.getActionItemsByUser(user.id);
          if (actionItems.success) {
            setHeartbeatActionItems(actionItems.data || []);
          }
        } catch (actionError) {
          console.log("No heartbeat action items found:", actionError.message);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistoryData();
  }, [user?.id, reloadKey]);

  const today = dayjs().format("YYYY-MM-DD");

  const getAllActivities = () => {
    const activities = [];

    services?.forEach((service) => {
      activities.push({ type: "service", date: service?.Service?.date, data: service });
    });

    heartbeatResponses?.forEach((response) => {
      activities.push({
        type: "heartbeat_survey",
        date: response?.submittedAt || response?.createdAt,
        data: response,
      });
    });

    heartbeatActionItems?.forEach((item) => {
      activities.push({ type: "heartbeat_action", date: item?.createdAt, data: item });
    });

    if (user?.createdAt) {
      activities.push({ type: "account_created", date: user.createdAt, data: user });
    }

    // Most recent first
    return activities.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
  };

  const allActivities = getAllActivities();

  if (loading) {
    return (
      <div aria-busy="true" className="flex flex-col gap-4 py-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} lines={2} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={getApiErrorMessage(error, "Activity history could not be loaded.")}
        onRetry={() => setReloadKey((key) => key + 1)}
      />
    );
  }

  if (allActivities.length === 0) {
    return <EmptyState variant="inline" title="No activity history yet" />;
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-4 border-b border-line pb-2 text-xs font-medium uppercase tracking-wide text-fg-muted">
        <span>Activity</span>
        <span>Date and Time</span>
      </div>
      <ul className="divide-y divide-line">
        {allActivities.map((activity, index) => {
          const Icon = ACTIVITY_ICONS[activity.type];
          return (
            <li key={`${activity.type}-${index}`} className="grid grid-cols-2 gap-4 py-4">
              <div className="flex min-w-0 gap-3">
                {Icon && (
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-subtle text-fg-muted">
                    <Icon aria-hidden="true" className="h-4 w-4" />
                  </span>
                )}
                <div className="min-w-0">
                  <ActivityDetails activity={activity} />
                </div>
              </div>
              <div className="text-sm">
                <p className="font-semibold text-fg">{formatDate(activity.date)}</p>
                <p className="text-fg-muted">
                  <ActivityTime activity={activity} today={today} />
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default History;
